import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Grid,
  Icon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core';
import { DesignedInfoDialog } from 'components';
import { getEavAttributeApi, updateEavAttributeApi, addEavAttributeApi, deleteEavAttributeApi } from 'api';
import { useStyles } from '../styles';
import QuoteMarketingSegmentsFilter from './QuoteMarketingSegmentsFilter';
import QuoteMarketingSegmentsItem from './QuoteMarketingSegmentsItem';


const defaultAddState = {
  isOpenModal: false,
  label: '',
  error: '',
  touched: false
};

const defaultEditState = {
  isOpenModal: false,
  label: '',
  value: '',
  error: '',
  touched: false
};

const QuoteMarketingSegments = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    profile
  } = useSelector((state: IReduxState) => state.user);
  const { options, isEavAttributeLoading } = useSelector((state: IReduxState) => state.eavAttributes);
  const permissions = profile?.config?.permissions || {};

  const [marketingSegments, setMarketingSegments] = useState(options);
  const [editMarketingSegment, setEditMarketingSegment] = useState(defaultEditState);
  const [addMarketingSegment, setAddMarketingSegment] = useState(defaultAddState);
  const [marketingSegmentsToDelete, setMarketingSegmentsToDelete] = useState([]);

  useEffect(() => {
    getEavAttributeApi({
      dispatch,
      entityType: 1,
      attributeCode: 'marketing_segments'
    });
  }, []);

  useEffect(() => {
    setMarketingSegments(options);
  }, [options]);

  const handleDeleteMarketingSegment = (valueToDelete) => {
    if (
      !marketingSegmentsToDelete.includes(valueToDelete)
    ) {
      setMarketingSegmentsToDelete((prevSegments) => [...prevSegments, valueToDelete]);
    }

    setMarketingSegments((prevSegments) =>
      prevSegments.filter((segment) => segment.value !== valueToDelete)
    );

    deleteEavAttributeApi({
      dispatch,
      entityType: 1,
      attributeCode: 'marketing_segments',
      optionId: valueToDelete
    });
  };

  const handleCreateName = ({target: {value}}) => {
    setAddMarketingSegment((prevSegment) => ({ ...prevSegment, label: value, touched: true  }));
    const isSameLabel = options.some((o) => o.label === value.trim());

    if (isSameLabel) {
      const error = 'Marketing segment name is already exist';
      setAddMarketingSegment((prevSegment) => ({ ...prevSegment, error }));
    } else if (!isSameLabel && addMarketingSegment.error.length) {
      setAddMarketingSegment((prevSegment) => ({ ...prevSegment, error: '' }));
    }
  };

  const handleEditName = ({target: {value}}) => {
    setEditMarketingSegment((prevSegment) => ({ ...prevSegment, label: value, touched: true  }));
    const optionsWithoutEdited = options.filter((o) => o.value !== editMarketingSegment.value);
    const isSameLabel = optionsWithoutEdited.some((o) => o.label === value.trim());

    if (isSameLabel) {
      const error = 'Marketing segment name is already exist';
      setEditMarketingSegment((prevSegment) => ({ ...prevSegment, error }));
    } else if (!isSameLabel && editMarketingSegment.error.length) {
      setEditMarketingSegment((prevSegment) => ({ ...prevSegment, error: '' }));
    }
  };

  const handleAddMarketingSegment = (label) => {
    addEavAttributeApi({
      dispatch,
      entityType: 1,
      attributeCode: 'marketing_segments',
      option: { label }
    });

    setAddMarketingSegment(defaultAddState);
  };

  const handleSaveEdits = () => {
    const { label, value } = editMarketingSegment;

    updateEavAttributeApi({
      dispatch,
      entityType: 1,
      attributeCode: 'marketing_segments',
      option: { label, value }
    });

    setMarketingSegments((prevSegments) =>
      prevSegments.map((marketingSegment) =>
        marketingSegment.value.toString() === value.toString() ? { ...marketingSegment, label } : marketingSegment
      )
    );
    setEditMarketingSegment(defaultEditState);
  };

  return (
    <div className="flex-grow h-full pb-8 px-8">
      <div className="flex items-center justify-between mb-4 mt-4">
        <Typography variant="h4">Marketing Segments</Typography>
        {permissions?.customersImport &&
        <Button
          color="primary"
          variant="contained"
          className="px-5"
          onClick={() => history.push('/customers/import')}
        >
          Upload Patients
        </Button>
        }
      </div>
      <Typography variant="h6" className="my-2">Search</Typography>
      <QuoteMarketingSegmentsFilter marketingSegments={options} setMarketingSegments={setMarketingSegments} />
      <Grid container spacing={2} alignItems="center" className={`${classes.marketingSegmentsDotted} mt-4`}>
        <Grid item xs={12}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Segment Name</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!isEavAttributeLoading && marketingSegments.map((segment, i) => (
                <QuoteMarketingSegmentsItem
                  key={`${i}-${segment.label}-${segment.value}`}
                  segment={segment}
                  handleEdit={() => setEditMarketingSegment((prevSegment) => ({ ...prevSegment, isOpenModal: true, label: segment.label, value: segment.value }))}
                  handleDeleteMarketingSegment={handleDeleteMarketingSegment}
                />
              ))}
            </TableBody>
          </Table>
        </Grid>
      </Grid>
      <Grid item xs={12} className="buttons-container mt-4">
        <Button
          color="primary"
          variant="outlined"
          onClick={() => setAddMarketingSegment((prevSegment) => ({ ...prevSegment, isOpenModal: true }))}
        >
          <Icon className="mr-2">add</Icon>
          Add Marketing Segment
        </Button>
      </Grid>
      <DesignedInfoDialog
        open={addMarketingSegment.isOpenModal}
        setOpen={() => setAddMarketingSegment(() => defaultAddState)}
      >
        <Typography variant="h5" color="primary" className="uppercase">
          Create Marketing Segment Name
        </Typography>
        <TextField
          required
          fullWidth
          variant="outlined"
          name="label"
          placeholder="Type Marketing Segment Name"
          className="max-w-400 my-6"
          value={addMarketingSegment.label}
          onChange={handleCreateName}
          error={addMarketingSegment.touched && Boolean(addMarketingSegment.error)}
          helperText={addMarketingSegment.touched && addMarketingSegment.error}
        />
        <div>
          <Button
            color="primary"
            variant="contained"
            disabled={!addMarketingSegment.label.length || !!addMarketingSegment.error.length}
            className="mb-4"
            onClick={() => handleAddMarketingSegment(addMarketingSegment.label)}
          >
            Create Marketing Segment
          </Button>
        </div>
      </DesignedInfoDialog>
      <DesignedInfoDialog
        open={editMarketingSegment.isOpenModal}
        setOpen={() => setEditMarketingSegment((prevSegment) => ({ ...prevSegment, isOpenModal: false }))}
      >
        <Typography variant="h5" color="primary" className="uppercase">
          Edit Marketing Segment Name
        </Typography>
        <TextField
          required
          fullWidth
          variant="outlined"
          name="label"
          placeholder="Type Marketing Segment Name"
          className="max-w-400 my-6"
          value={editMarketingSegment.label}
          onChange={handleEditName}
          error={editMarketingSegment.touched && Boolean(editMarketingSegment.error)}
          helperText={editMarketingSegment.touched && editMarketingSegment.error}
        />
        <div>
          <Button
            color="primary"
            variant="contained"
            disabled={!editMarketingSegment.label.length || !!editMarketingSegment.error.length}
            className="mb-4"
            onClick={handleSaveEdits}
          >
            Save
          </Button>
        </div>
      </DesignedInfoDialog>
    </div>
  );
};

export default QuoteMarketingSegments;
